import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { dateTime, rs } from '../format';
import { Async, BarChart, Button, HBars, Kpi, KpiGrid, PageHeader, Panel, Timeline } from '../ui';
import useFetch from '../useFetch';
import { exportResellers } from './exportResellers';
import { AddResellerModal, PayoutModal, ResellerDetailModal } from './AdminModals';
import ResellerTable from './ResellerTable';
import { useDash } from '../DashboardShell';

const today = () => new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

function attentionItems(a, totals) {
  const items = [];
  if (!a.webhook_secret_set) {
    items.push({ key: 'wh', title: 'Webhook secret not set', sub: 'The commission webhook accepts unsigned requests. Set WEBHOOK_SECRET here and on finOS.', chip: 'Security', bg: '#fdeeec', fg: '#9b2418' });
  }
  if (a.owed_resellers > 0) {
    items.push({ key: 'owed', to: '/admin/payouts', title: 'Payouts waiting', sub: `${rs(a.owed_total)} owed across ${a.owed_resellers} reseller${a.owed_resellers === 1 ? '' : 's'}.`, chip: String(a.owed_resellers), bg: '#fff4d9', fg: '#7a4d00' });
  }
  if (a.pending_approvals > 0) {
    items.push({ key: 'pend', to: '/admin/resellers?status=pending', title: 'Signups to review', sub: `${a.pending_approvals} reseller${a.pending_approvals === 1 ? ' is' : 's are'} waiting for approval.`, chip: String(a.pending_approvals), bg: '#fff4d9', fg: '#7a4d00' });
  }
  if (a.no_conversions > 0) {
    items.push({ key: 'idle', to: '/admin/resellers', title: 'Resellers with no conversions', sub: `${a.no_conversions} active reseller${a.no_conversions === 1 ? ' has' : 's have'} not had an approved application yet.`, chip: String(a.no_conversions), bg: '#eaf0fb', fg: '#0a2540' });
  }
  if (a.pending_approvals === 0) {
    items.push({ key: 'clear', title: 'Pending approvals', sub: 'No signups are waiting for review.', chip: 'Clear', bg: '#e6f6ee', fg: '#0a6038' });
  }
  return items;
}

const Overview = () => {
  const { notify } = useDash();
  const overview = useFetch('/admin/overview');
  const list = useFetch('/admin/resellers', { limit: 5 });
  const [adding, setAdding] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [payFor, setPayFor] = useState(null);

  const reload = () => {
    overview.reload();
    list.reload();
  };
  const startPay = (r) => {
    setViewId(null);
    setPayFor(r);
  };

  return (
    <>
      <PageHeader eyebrow={today()} title="Overview">
        <Button onClick={() => exportResellers({}, notify)}>Export CSV</Button>
        <Button variant="primary" onClick={() => setAdding(true)}>Add reseller</Button>
      </PageHeader>

      <Async state={overview}>
        {(o) => {
          const t = o.totals;
          const series = o.monthly.map((m) => ({ label: m.label, a: m.earned, b: m.paid }));
          const paidShare = t.commission_earned ? `${Math.round((t.commission_paid / t.commission_earned) * 100)}% of earned` : 'Nothing earned yet';
          return (
            <>
              <KpiGrid>
                <Kpi label="Resellers" value={t.resellers} note={`${t.active} active, ${t.pending} pending${t.suspended ? `, ${t.suspended} inactive` : ''}`} noteTone="good" />
                <Kpi label="Conversions" value={t.conversions.toLocaleString('en-US')} note="All time" />
                <Kpi label="Commission earned" value={rs(t.commission_earned)} note="All time" />
                <Kpi label="Paid out" value={rs(t.commission_paid)} note={paidShare} />
                <Kpi tone="accent" label="Balance owed" value={rs(t.commission_owed)} note={o.attention.owed_resellers ? `${o.attention.owed_resellers} reseller${o.attention.owed_resellers === 1 ? '' : 's'} waiting` : 'All paid up'} />
              </KpiGrid>

              <div className="ds-row">
                <Panel wide title="Commission earned and paid" subtitle="Rs, last 6 months. The gap is what you still owe." >
                  <BarChart
                    series={series}
                    aLabel="Earned"
                    bLabel="Paid"
                    ariaLabel={`Commission earned and paid per month: ${o.monthly.map((m) => `${m.label} earned ${m.earned}, paid ${m.paid}`).join('; ')}`}
                  />
                </Panel>
                <Panel title="Needs your attention">
                  <ul className="ds-attn">
                    {attentionItems(o.attention, t).map((i) => (
                      <li key={i.key} style={{ background: i.bg }}>
                        <div>
                          <strong>{i.to ? <Link to={i.to}>{i.title}</Link> : i.title}</strong>
                          <span className="sub">{i.sub}</span>
                        </div>
                        <span className="chip" style={{ color: i.fg }}>{i.chip}</span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              </div>

              <Panel title="Resellers" action={<Link to="/admin/resellers" className="ds-btn is-sm">View all</Link>}>
                <Async state={list}>
                  {(l) => (
                    <>
                      <ResellerTable simple rows={l.items} onView={(r) => setViewId(r.id)} onPay={startPay} emptyText="Add your first reseller to get started." />
                      <p className="ds-foot">Showing {l.items.length} of {l.total} resellers</p>
                    </>
                  )}
                </Async>
              </Panel>

              <div className="ds-row">
                <Panel title="Top resellers by commission" subtitle="Rs earned, all time">
                  {o.top_resellers.every((r) => r.earned === 0)
                    ? <p className="ds-foot">No commission has been earned yet.</p>
                    : <HBars rows={o.top_resellers.map((r) => ({ key: r.id, label: r.name, value: r.earned, text: rs(r.earned) }))} />}
                </Panel>
                <Panel title="Recent activity" action={<Link to="/admin/activity" className="ds-btn is-sm">View all</Link>}>
                  <Timeline events={o.recent_activity.map((e) => ({ ...e, when: dateTime(e.at) }))} empty="Activity appears here as applications and payouts come in." />
                </Panel>
              </div>
            </>
          );
        }}
      </Async>

      <AddResellerModal open={adding} onClose={() => setAdding(false)} onDone={reload} />
      <ResellerDetailModal resellerId={viewId} onClose={() => setViewId(null)} onChanged={reload} onPay={startPay} />
      <PayoutModal open={!!payFor} reseller={payFor} onClose={() => setPayFor(null)} onDone={reload} />
    </>
  );
};

export default Overview;
